import React, { useState, useEffect, useMemo, Fragment, useRef } from "react";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from "react-router-dom";
import WebService from "@util/webService";
import { setCodes } from '@redux/actions/codeAction'
import { useSelector, useDispatch } from 'react-redux'

import Footer from "@components/part/Footer";
import {
  Grid,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  Button,
  Checkbox,
  RadioGroup,
  IconButton,
} from '@mui/material'
import { isAdmin } from "@util/helper";
import { comma } from '@src/util/helper'
import { getProductMonth } from '@pages/common/common'
import { LogoutOutlined, Person } from '@mui/icons-material'
import ArchitectureOutlined from '@mui/icons-material/ArchitectureOutlined'

// 가격 카드 정상가 기준: 월 10만원 (계약서 제4조 정상가격과 동일)
const MONTHLY_LIST_PRICE = 100000
const priceCardColors = ['#dbf7fc', '#dce5fc', '#e6e0fc', '#fcebdc']

// Home 마운트 순번: main.js 주소를 마운트마다 다르게 만들어 Helmet 이 스크립트를 새로 넣도록 함
let homeMountSeq = 0

const Home = () => {
  // main.js(AOS, Swiper, 헤더/메뉴 핸들러)는 실행 시점의 DOM 에만 적용됨
  // 다른 화면에서 돌아오면 Home DOM 이 새로 그려지므로, 마운트마다 main.js 를 다시 실행해 새 DOM 을 초기화
  const [mainJsSrc] = useState(() => `/src/js/main.js?home=${++homeMountSeq}`)
  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const account = useSelector((state) => state.accountReducer)
  const codes = useSelector((state) => state.codeReducer)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  // 가격 카드 CSS 는 1280px 이하에서 세로 1열, 그 이상에서는 카드 2장 기준이므로 넓은 화면에서 2x2 로 배치
  const isWidePrice = useMediaQuery('(min-width:1281px)')
  const branchStatCodes = codes.filter((r) => r.codeGroupId === 'B001')
  const [branches, set_branches] = useState([])
  const [reviews, set_reviews] = useState([])
  // /public/info 조회 완료 여부: 해시 스크롤은 리뷰/가격 카드를 그린 뒤에 해야 위치가 맞음
  const [loaded, set_loaded] = useState(false)

  // 코드, 노출 지점, 노출 리뷰를 한 번에 조회 (PublicController.getInfo)
  const fetchInfo = async () => {
    const url = '/public/info'
    const resp = await WebService.get(url)
    if (resp.repCode !== 'ack') {
      console.log('fetch info error=>', resp.repMessage)
      return
    }
    const repMessage = JSON.parse(resp.repMessage)
    return {
      codes: JSON.parse(repMessage.codes),
      branches: JSON.parse(repMessage.branches),
      reviews: JSON.parse(repMessage.reviews),
    }
  }

  const onLoad = async() => {
    const info = await fetchInfo()
    if(info) {
      dispatch(setCodes(info.codes))
      // id=-1 은 '호실 미배정' ghost 지점이므로 제외, 등록 순으로 표시
      set_branches(info.branches.filter((b) => b.id > 0).sort((a, b) => a.id - b.id))
      set_reviews(info.reviews)
    }
    // 조회 실패해도 해시 스크롤은 진행
    set_loaded(true)
  }

  // 별점은 1~5 범위로 보정: 범위 밖 값이면 repeat 에서 오류가 나므로 방어
  const clampStar = (star) => Math.min(5, Math.max(0, Number(star) || 0))

  // 리뷰 슬라이더는 main.js 에서 페이지 로드 시 초기화되므로, 리뷰를 그린 뒤 Swiper 에 슬라이드 변경을 반영
  useEffect(() => {
    document.querySelector('.reviewSwiper')?.swiper?.update()
  }, [reviews])

  const formatPhone = (b) => [b.phoneDial, b.phone1, b.phone2].filter((s) => s?.trim()).join('-')

  // 기본 주소(addr1)가 없으면 빈 문자열: 우편번호/상세 주소만으로는 주소 라인을 표시하지 않음
  const formatAddr = (b) => {
    if (!b.addr1?.trim()) {
      return ''
    }
    return [b.zipCode ? `(${b.zipCode})` : '', b.addr1, b.addr2].filter((s) => s?.trim()).join(' ')
  }

  // 본점(hq_yn=Y)은 왼쪽에 1개만 표시, 여러 개면 id가 가장 작은 지점 사용
  // 본점이 없거나 미노출이면 기존 마곡 직영점 정보를 기본값으로 표시
  const hqBranch = branches.find((b) => b.hqYn === 'Y')
  const hqInfo = hqBranch
    ? { title: hqBranch.branchName, addr: formatAddr(hqBranch), phone: formatPhone(hqBranch) }
    : { title: '마곡 직영점', addr: '서울 강서구 마곡서로 101 (마곡동,동익드미라벨)', phone: '010-2608-8615' }
  const otherBranches = branches.filter((b) => b.id !== hqBranch?.id)

  // 가격 카드: 기간 상품(개월 수가 있는 R001 상품)을 순번대로, 자유결제(P100)는 마지막에 표시
  const periodProducts = codes
    .filter((r) => r.codeGroupId === 'R001' && getProductMonth(r.code) > 0)
    .sort((a, b) => a.sqNum - b.sqNum)
  const customProduct = codes.find((r) => r.codeGroupId === 'R001' && r.code === 'P100')
  // 넓은 화면: 한 줄에 2장씩, 왼쪽 카드만 오른쪽 간격
  const priceCardStyle = (idx) => (isWidePrice ? { width: 'calc(50% - 10px)', marginRight: idx % 2 === 0 ? 20 : 0 } : {})
  // CSS 에 색 규칙이 3번째 카드까지만 있어 자유결제 카드 색은 직접 지정
  const customAccent = 'rgb(246, 200, 160)'

  useEffect(() => {
    onLoad()
  },[])
  // 리뷰/가격 카드를 그린 뒤 해시 위치로 이동
  // 먼저 스크롤하면 나중에 그려지는 리뷰 섹션 높이만큼 #contact 가 밀려 위쪽에 멈춤
  useEffect(() => {
    if (!loaded || !location.hash) {
      return
    }
    document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth' })
  }, [location, loaded])

  return (
    <>
      <Helmet>
        <script src={mainJsSrc}></script>
      </Helmet>

      <header id="header">
        <div className="container">
          <div className="logo">
            <a href="/">
              <img src="/src/img/logo_w.svg"
                alt="비상주 오피스"
                className="white"
              />
              <img src="/src/img/logo.svg"
                alt="비상주 오피스"
                className="black"
              />
            </a>
          </div>
          <div className="gnb">
            <nav className="gnb_nav">
              <ul>
                <li>
                  <a href="#">비상주 오피스</a>
                  <div className="dropdown">
                    <a href="#about">비상주오피스 소개</a>
                    <a href="#progress">계약절차안내</a>
                    <a href="#contact">계약,결제하기</a>
                  </div>
                </li>
                <li>
                  <a href="#bundle">결합 할인 서비스</a>
                </li>
                <li>
                  <a href="#location">오시는 길</a>
                </li>
              </ul>
            </nav>
            <div className="inquiry">
              <a href="#contact">빠른 계약하기</a>
            </div>

            {
              account?.user?.id ? (
                <>
                  {
                    isAdmin(account) ? (
                      <div>
                        <IconButton onClick={() => navigate('/admin')}>
                          <ArchitectureOutlined style={{color:'#9e9e9e'}}/>
                        </IconButton>
                      </div>
                    ) : null
                  }
                  <div>
                    <IconButton onClick={() => navigate('/logout')}>
                      <LogoutOutlined style={{color:'#9e9e9e'}}/>
                    </IconButton>
                  </div>
                </>

              ) : null
            }


          </div>

          <div className="mo_btn">
            <a href="#" id="moBtn">
              <img src="/src/img/nav_on_w.svg" className="white" />
              <img src="/src/img/nav_on_b.svg" className="black" />
            </a>
          </div>
        </div>
        <div className="side_menu">
          <div className="box">
            <div className="logo">
              <a href="/">
                <img src="/src/img/logo_w.svg" alt="비상주 오피스" />
              </a>
            </div>
            <nav className="side_nav">
              <ul>
                <li>
                  <a href="#">비상주 오피스</a>
                  <div className="dropdown">
                    <a href="#about">비상주오피스 소개</a>
                    <a href="#progress">계약절차안내</a>
                    <a href="#contact">계약,결제하기</a>
                  </div>
                </li>
                <li>
                  <a href="#bundle">결합 할인 서비스</a>
                </li>
                <li>
                  <a href="#location">오시는 길</a>
                </li>
              </ul>
            </nav>
            <div className="inquiry">
              <a href="#contact">빠른 계약하기</a>
            </div>


            <a href="#" id="moCan">
              <img src="/src/img/nav_can.svg" />
            </a>
          </div>
        </div>
      </header>
      <section className="main_visual">
        {/* Slider main container */}
        <div className="swiper mainSwiper">
          {/* Additional required wrapper */}
          <div className="swiper-wrapper">
            {/* Slides */}
            <div className="swiper-slide slide_1">
              <div className="text_box">
                <h2
                  data-aos="fade-up"
                  data-aos-duration={1500}
                  data-aos-delay={100}
                >
                  저렴한 비용의
                  <br />
                  <b>프리미엄 비상주 사무실</b>
                </h2>
                <a
                  href="#contact"
                  data-aos="fade-up"
                  data-aos-duration={1500}
                  data-aos-delay={300}
                >
                  빠른 계약 바로가기
                </a>
              </div>
            </div>
            <div className="swiper-slide slide_2">
              <div className="text_box">
                <h2
                  data-aos="fade-up"
                  data-aos-duration={1500}
                  data-aos-delay={100}
                >
                  사업자 등록을 위한
                  <br />
                  <b>주소지가 해결 솔루션!</b>
                </h2>
                <a
                  href="#contact"
                  data-aos="fade-up"
                  data-aos-duration={1500}
                  data-aos-delay={300}
                >
                  빠른 계약 바로가기
                </a>
              </div>
            </div>
            <div className="swiper-slide slide_3">
              <div className="text_box">
                <h2
                  data-aos="fade-up"
                  data-aos-duration={1500}
                  data-aos-delay={100}
                >
                  사업자가 나오지 않으면
                  <br />
                  <b>100% 환불 보장!</b>
                </h2>
                <a
                  href="#contact"
                  data-aos="fade-up"
                  data-aos-duration={1500}
                  data-aos-delay={300}
                >
                  빠른 계약 바로가기
                </a>
              </div>
            </div>
          </div>
          {/* If we need navigation buttons */}
          <div className="swiper-button-prev" />
          <div className="swiper-button-next" />
          <div id="scroll-down-animation">
            <a href="#about">
              <span className="mouse">
                <span className="move" />
              </span>
            </a>
          </div>
        </div>
      </section>
      <section className="mc_1" id="about">
        <div className="container">
          <div className="main_title">
            <span
              data-aos="fade-up"
              data-aos-duration={1500}
              data-aos-delay={100}
            >
              About Us
            </span>
            <h2
              data-aos="fade-up"
              data-aos-duration={1500}
              data-aos-delay={200}
            >
              비상주 사무실이란?
            </h2>
            <p data-aos="fade-up" data-aos-duration={1500} data-aos-delay={300}>
              상주 필요성이 없지만 사업자 등록이 필요한 분을 위해, 주소 등을
              제공하는 서비스를 말합니다.
            </p>
          </div>
          <div className="img_box">
            <div
              className="left"
              data-aos="fade-up"
              data-aos-duration={1500}
              data-aos-delay={400}
            >
              <img src="/src/img/mc_1_img_1.jpg" />
            </div>
            <div
              className="right"
              data-aos="fade-up"
              data-aos-duration={1500}
              data-aos-delay={500}
            >
              <img src="/src/img/mc_1_img_2.jpg" />
            </div>
          </div>
        </div>
      </section>
      <section className="mc_2">
        <div className="container">
          <div className="main_title">
            <span
              data-aos="fade-up"
              data-aos-duration={1500}
              data-aos-delay={100}
            >
              Who needs
            </span>
            <h2
              data-aos="fade-up"
              data-aos-duration={1500}
              data-aos-delay={200}
            >
              비상주 사무실 어떤 분들에게 필요할까요?
            </h2>
          </div>
          <div className="item_box">
            <ul>
              <li
                data-aos="fade-up"
                data-aos-duration={1500}
                data-aos-delay={100}
              >
                <div className="img_box">
                  <img src="/src/img/mc_2_img_1.jpg" />
                </div>
                <p>
                  사업자 등록을 할 수 있는
                  <br />
                  주소지가 필요하신 창업자
                </p>
              </li>
              <li
                data-aos="fade-up"
                data-aos-duration={1500}
                data-aos-delay={200}
              >
                <div className="img_box">
                  <img src="/src/img/mc_2_img_2.jpg" />
                </div>
                <p>
                  외근, 잦은 출장, 재택근무등으로
                  <br />
                  사무실 업무 빈도가 낮은 사업자
                </p>
              </li>
              <li
                data-aos="fade-up"
                data-aos-duration={1500}
                data-aos-delay={300}
              >
                <div className="img_box">
                  <img src="/src/img/mc_2_img_3.jpg" />
                </div>
                <p>초기비용이 부담스러운 사업자</p>
              </li>
              <li
                data-aos="fade-up"
                data-aos-duration={1500}
                data-aos-delay={400}
              >
                <div className="img_box">
                  <img src="/src/img/mc_2_img_4.jpg" />
                </div>
                <p>
                  집이아닌 다른곳으로 우편물을
                  <br />
                  받고자하는 사업자
                </p>
              </li>
              <li
                data-aos="fade-up"
                data-aos-duration={1500}
                data-aos-delay={500}
              >
                <div className="img_box">
                  <img src="/src/img/mc_2_img_5.jpg" />
                </div>
                <p>
                  온라인 쇼핑몰 등<br />
                  온라인 업무가 많은 사업자
                </p>
              </li>
              <li
                data-aos="fade-up"
                data-aos-duration={1500}
                data-aos-delay={600}
              >
                <div className="img_box">
                  <img src="/src/img/mc_2_img_6.jpg" />
                </div>
                <p>
                  현재 사업장에서 추가로
                  <br />
                  사업장을 늘리고 싶은 사업자
                </p>
              </li>
            </ul>
          </div>
        </div>
      </section>
      <section className="mc_3">
        <div className="container">
          <div className="title_box">
            <div className="main_title">
              <span
                data-aos="fade-up"
                data-aos-duration={1500}
                data-aos-delay={100}
              >
                Real Review
              </span>
              <h2
                data-aos="fade-up"
                data-aos-duration={1500}
                data-aos-delay={200}
              >
                비상주 오피스 고객님들의 실제후기
              </h2>
            </div>
            <div
              className="btn_box"
              data-aos="fade-up"
              data-aos-duration={1500}
              data-aos-delay={200}
            >
              <div className="prev_1 arrow_btn">
                <img src="/src/img/prev.png" />
              </div>
              <div className="next_1 arrow_btn">
                <img src="/src/img/next.png" />
              </div>
            </div>
          </div>
          <div
            className="review_box"
            data-aos="fade-up"
            data-aos-duration={1500}
            data-aos-delay={300}
          >
            <div className="swiper reviewSwiper">
              {/* Additional required wrapper */}
              <div className="swiper-wrapper">
                {/* Slides: 노출(display_yn=Y) 리뷰, PublicController.getInfo 의 reviews */}
                {reviews.map((r) => (
                  <div className="swiper-slide" key={r.id}>
                    <div className="box">
                      <div className="profile">
                        <div className="top">
                          <div className="pic">
                            <img src="/src/img/user_profile.png" />
                          </div>
                          <div className="info">
                            <p>{r.userAlias}</p>
                            <span>계약기간: {r.prodAlias}</span>
                          </div>
                        </div>
                        <div className="ratings">
                          <p>
                            {'★'.repeat(clampStar(r.star))}
                            {/* 빈 별: .ratings span 스타일이 적용되지 않도록 i 태그 사용 */}
                            <i style={{ fontStyle: 'normal', color: '#d0d0d0' }}>{'★'.repeat(5 - clampStar(r.star))}</i>
                          </p>
                          <span>{r.star}</span>
                        </div>
                      </div>
                      <article style={{ whiteSpace: 'pre-line' }}>{r.content}</article>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="mc_4" id="progress">
        <div className="container">
          <div
            className="text_box"
            data-aos="fade-up"
            data-aos-duration={1500}
            data-aos-delay={100}
          >
            <h2>비상주 오피스 계약 절차</h2>
            <h3>
              24시간 빠르고 쉬운
              <br />
              비대면 계약제공
            </h3>
            <p>
              계약서 작성과 결제를 10분 이내에 완성하는 간편함!
              <br />
              신규사업자의 경우 계약서 작성 후, 등록 신청 시 변경계약 지원까지!!
            </p>
            <span>
              * 신규법인 등 필요한 경우 대표자 이름으로 계약 후, 법인명 또는
              상호명으로 변경계약 지원
            </span>
            <span>
              * 비상주 오피스는 유흥업소 및 불법 게임 등의 불법 시설은 계약이
              불가 합니다.
            </span>
          </div>
          <div
            className="item_box"
            data-aos="fade-up"
            data-aos-duration={1500}
            data-aos-delay={200}
          >
            <ul>
              <li>
                <div className="img_box">
                  <img src="/src/img/mc_4_img_1.png" />
                </div>
                <p>
                  <span>1</span>빠른 계약서 작성
                </p>
              </li>
              <li className="right">
                <div className="img_box">
                  <img src="/src/img/mc_4_img_2.png" />
                </div>
                <p>
                  <span>2</span>결제 진행
                </p>
              </li>
              <li>
                <div className="img_box">
                  <img src="src/img/mc_4_img_3.png" />
                </div>
                <p>
                  <span>3</span>계약서 출력
                </p>
              </li>
              <li className="right">
                <div className="img_box">
                  <img src="/src/img/mc_4_img_4.png" />
                </div>
                <p>
                  <span>4</span>사업자 등록(정정) 진행
                </p>
              </li>
            </ul>
          </div>
        </div>
      </section>
      <section className="mc_5" id="contact">
        <div className="container">
          <div className="main_title">
            <span
              data-aos="fade-up"
              data-aos-duration={1500}
              data-aos-delay={100}
            >
              Contract
            </span>
            <h2
              data-aos="fade-up"
              data-aos-duration={1500}
              data-aos-delay={200}
            >
              빠르게 비상주 사무실 계약하기
            </h2>
            <p data-aos="fade-up" data-aos-duration={1500} data-aos-delay={300}>
              무보증 / 무예치금으로 비용을 절감 하세요.
            </p>
          </div>
          <div className="price_box">
            <ul style={isWidePrice ? { flexWrap: 'wrap', rowGap: 20 } : undefined}>
              {periodProducts.map((p, idx) => {
                const month = getProductMonth(p.code)
                const price = +(p.note1 ?? 0)
                const listPrice = MONTHLY_LIST_PRICE * month
                const discount = listPrice > 0 ? Math.round((1 - price / listPrice) * 100) : 0
                return (
                  <li
                    key={p.code}
                    style={{ background: priceCardColors[idx % priceCardColors.length], ...priceCardStyle(idx) }}
                    data-aos="fade-up"
                    data-aos-duration={1500}
                    data-aos-delay={400 + idx * 100}
                  >
                    <div className="price">
                      <span>{month}개월</span>
                      <p>{comma(listPrice)}원</p>
                      <b>
                        {comma(price)}원 {discount > 0 ? <span>{discount}% 할인</span> : null}
                      </b>
                    </div>
                    <div className="btn">
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault()
                          navigate(`/contract1/${p.code}`)
                        }}
                      >
                        계약하기
                      </a>
                    </div>
                  </li>
                )
              })}
              {customProduct ? (
                <li
                  style={{
                    background: priceCardColors[periodProducts.length % priceCardColors.length],
                    ...priceCardStyle(periodProducts.length),
                  }}
                  data-aos="fade-up"
                  data-aos-duration={1500}
                  data-aos-delay={400 + periodProducts.length * 100}
                >
                  <div className="price">
                    <span style={{ color: 'rgb(176, 104, 50)' }}>{customProduct.name}</span>
                    {/* 정상가 줄 자리: 다른 카드와 높이를 맞추기 위해 빈 줄 유지 */}
                    <p style={{ textDecoration: 'none' }}>&nbsp;</p>
                    <b>
                      원하는 금액 <span style={{ background: customAccent }}>1만원 단위</span>
                    </b>
                  </div>
                  <div className="btn">
                    <a
                      href="#"
                      style={{ background: customAccent }}
                      onClick={(e) => {
                        e.preventDefault()
                        navigate('/contract-custom')
                      }}
                    >
                      결제하기
                    </a>
                  </div>
                </li>
              ) : null}
            </ul>
            {/* 우편물 실물 발송 서비스 안내: 서비스 종료로 제외 */}
          </div>
        </div>
      </section>
      <section className="mc_6" id="bundle">
        <div className="container">
          <div className="main_title">
            <span
              data-aos="fade-up"
              data-aos-duration={1500}
              data-aos-delay={100}
            >
              Bundle Service
            </span>
            <h2
              data-aos="fade-up"
              data-aos-duration={1500}
              data-aos-delay={200}
            >
              결합 할인 서비스
            </h2>
            <p data-aos="fade-up" data-aos-duration={1500} data-aos-delay={300}>
              세무서비스 결합으로 더욱 더 큰 혜택을 받으세요.
            </p>
          </div>
          <div className="item_box">
            <div
              className="text_box"
              data-aos="fade-up"
              data-aos-duration={1500}
              data-aos-delay={400}
            >
              <h3>세무 서비스 + 비상주 사무실</h3>
              <p>
                세무서비스 결합시 비상주사무실 할인과 세무, 회계 상담 서비스까지
                제공해드려요.
              </p>
              <ul>
                <li>
                  <p>혜택 1</p>
                  <span>
                    비상주 사무실 10,000원
                    <br />월 계약기간 동안 쭉~ 할인제공
                  </span>
                </li>
                <li>
                  <p>혜택 2</p>
                  <span>
                    세무, 회계 상담 서비스 제공
                  </span>
                </li>
              </ul>
              <span>
                * 비상주사무실 할인제공은 기본계약 체결 후 부분환불 혹은
                현금지원 방식으로 진행되며, 세무서비스는 신규 사업자기준 기장료
                개인 월 8만월 / 법인 월 12만원 기본계약조건이며 조정료
                별도입니다. 월기장료는 매출액또는 자산가액 기준으로 산정되며
                상담후 또는 익년도에 변동될수 있습니다.
              </span>
            </div>

            {/* <div */}
            {/*   className="form_box" */}
            {/*   data-aos="fade-up" */}
            {/*   data-aos-duration={1500} */}
            {/*   data-aos-delay={500} */}
            {/* > */}
            {/*   <div className="title"> */}
            {/*     <h3>결합 할인 서비스 상담 신청</h3> */}
            {/*     <span>결합 서비스를 신청해보세요.</span> */}
            {/*   </div> */}
            {/*   <form> */}
            {/*     <div className="form_row"> */}
            {/*       <input type="text" placeholder="성함" /> */}
            {/*     </div> */}
            {/*     <div className="form_row"> */}
            {/*       <input type="tel" placeholder="연락처" /> */}
            {/*     </div> */}
            {/*     <div className="form_row"> */}
            {/*       <textarea placeholder="문의사항" defaultValue={""} /> */}
            {/*     </div> */}
            {/*     <div className="btn_box"> */}
            {/*       <div className="privacy"> */}
            {/*         <label> */}
            {/*           <input type="checkbox" /> */}
            {/*           <span> */}
            {/*             개인 정보 취급방침에 동의 합니다. */}
            {/*             <a href="#privacy_modal" rel="modal:open"> */}
            {/*               [내용확인] */}
            {/*             </a> */}
            {/*           </span> */}
            {/*         </label> */}
            {/*       </div> */}
            {/*       <button type="button">상담 신청</button> */}
            {/*     </div> */}
            {/*   </form> */}
            {/* </div> */}

          </div>
        </div>
      </section>
      <section className="mc_7" id="location">
        <div className="container">
          <div
            className="text_box"
            data-aos="fade-up"
            data-aos-duration={1500}
            data-aos-delay={100}
            // 데스크톱: CSS 고정 폭(35%) 대신 오른쪽 지점 목록과 반씩 나눠 사용
            style={isMobile ? undefined : { flex: '1 1 0', width: 'auto', marginRight: 40 }}
          >
            <div className="left">
              <span>Location</span>
              <h3>{hqInfo.title} 오시는 길</h3>
            </div>
            <div className="right">
              {hqInfo.addr ? (
                <p>
                  <b>주소</b>
                  <span>{hqInfo.addr}</span>
                </p>
              ) : null}
              {hqInfo.phone ? (
                <p>
                  <b>문의 전화</b>
                  <span>{hqInfo.phone}</span>
                </p>
              ) : null}
              <a href="#contact">계약 바로가기</a>
            </div>
          </div>
          {/* 지도 대신 본점을 제외한 노출(display_yn=Y) 지점 목록: 래퍼는 첫 렌더부터 존재하도록 두어 AOS 대상에서 빠지지 않게 함 */}
          {/* 데스크톱: 본점 정보와 반씩 나눈 오른쪽 영역에 세로로 쌓음 */}
          <div
            className="branch_box"
            data-aos="fade-up"
            data-aos-duration={1500}
            data-aos-delay={200}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              alignContent: 'start',
              gap: 20,
              ...(isMobile ? { marginTop: 40 } : { flex: '1 1 0', alignSelf: 'flex-start' }),
            }}
          >
            {otherBranches.map((b) => (
              <div
                key={b.id}
                style={{
                  background: '#f0f0f0',
                  borderRadius: 30,
                  padding: '28px 30px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <h4 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{b.branchName}</h4>
                  <span
                    style={{
                      fontSize: 13,
                      color: '#1a4fd8',
                      border: '1px solid #1a4fd8',
                      borderRadius: 20,
                      padding: '2px 10px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {branchStatCodes.find((c) => c.code === b.statCode)?.name ?? b.statCode}
                  </span>
                </div>
                {formatAddr(b) ? (
                  <p style={{ display: 'flex', gap: 10, marginBottom: 8, lineHeight: 1.6 }}>
                    <b style={{ flex: '0 0 60px' }}>주소</b>
                    <span>{formatAddr(b)}</span>
                  </p>
                ) : null}
                {formatPhone(b) ? (
                  <p style={{ display: 'flex', gap: 10, lineHeight: 1.6 }}>
                    <b style={{ flex: '0 0 60px' }}>문의 전화</b>
                    <a href={`tel:${b.phone}`}>{formatPhone(b)}</a>
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />

    </>
  );
};

export default Home;
